import { useEffect, useState, useContext } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { Button, DropDownMUI, PopOverView } from "../../components";
import {
  FiChevronDown,
  FiChevronUp,
  FiPrinter,
  FiDownload,
  FiArrowLeft,
} from "react-icons/fi";
import { AiOutlineInfoCircle } from "react-icons/ai";
import { BiEditAlt } from "react-icons/bi";
import CircularProgress from "@material-ui/core/CircularProgress";
import firebase from "../../config/firebase";
import { UserContext } from "../../Context/UserContext";
import { toast } from "react-toast";
import drawerTemplate1 from "../../hoc/drawerTemplate1";
import "./BillDetail.scss";

const BillDetail = () => {
  const { poId, billId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const userContext = useContext(UserContext);
  const [bill, setBill] = useState(location.state?.billData || {});
  const [isReadOnly, setIsReadOnly] = useState(
    location.state?.readOnly || true
  );
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSpinner, setSpinner] = useState(false);
  const [remarks, setRemarks] = useState(bill.remarks || "");
  const [currentTab, setCurrentTab] = useState(0); // 0: Work Order Items, 1: Additional Items

  // Editable state
  const [editedBill, setEditedBill] = useState({});

  useEffect(() => {
    if (!bill.poId && poId && billId) {
      fetchBillData();
    } else {
      setEditedBill(JSON.parse(JSON.stringify(bill))); // Deep copy
    }
  }, [poId, billId]);

  const fetchBillData = async () => {
    setSpinner(true);
    try {
      const billingRef = firebase
        .database()
        .ref(`billingInformation/${poId}/${billId}`);
      const snapshot = await billingRef.once("value");
      const billData = snapshot.val();

      if (billData) {
        const fetchedBill = {
          ...billData,
          poId,
          billId,
        };

        setBill(fetchedBill);
        setEditedBill(JSON.parse(JSON.stringify(fetchedBill))); // Deep copy
        setRemarks(fetchedBill.remarks || "");
      } else {
        toast.error("Bill not found!");
        history.push("/Billing");
      }
    } catch (error) {
      console.error("Error fetching bill data:", error);
      toast.error("Error fetching bill data!");
    }
    setSpinner(false);
  };

  const handleSave = async () => {
    try {
      setSpinner(true);

      // Update with edited values
      const dataToSave = {
        ...bill,
        ...editedBill,
        remarks,
      };

      if (isEditMode) {
        // If admin is editing
        if (
          userContext.user.accountType === "Admin" ||
          userContext.user.accountType === "Owner"
        ) {
          dataToSave.status = "Approved";
          dataToSave.approvedBy =
            userContext.user.name || userContext.user.email;
          dataToSave.approvedAt = new Date().toLocaleString();
        }
        // If checker is editing
        else if (userContext.user.accountType === "Finance") {
          dataToSave.status = "Checked";
          dataToSave.checkedBy =
            userContext.user.name || userContext.user.email;
          dataToSave.checkedAt = new Date().toLocaleString();
        }
      }

      // Remove unnecessary fields
      delete dataToSave.poId;
      delete dataToSave.billId;

      const billingRef = firebase
        .database()
        .ref(`billingInformation/${poId}/${billId}`);
      await billingRef.set(dataToSave);

      // Update local state
      setBill({
        ...dataToSave,
        poId,
        billId,
      });

      toast.success("Bill successfully saved!");
      setIsEditMode(false);
    } catch (error) {
      console.error("Error saving bill:", error);
      toast.error("Error saving bill!");
    }
    setSpinner(false);
  };

  const handleApprove = async () => {
    try {
      setSpinner(true);

      // Mark as approved
      const dataToSave = {
        ...bill,
        status: "Approved",
        approvedBy: userContext.user.name || userContext.user.email,
        approvedAt: new Date().toLocaleString(),
        remarks,
      };

      // Remove unnecessary fields
      delete dataToSave.poId;
      delete dataToSave.billId;

      const billingRef = firebase
        .database()
        .ref(`billingInformation/${poId}/${billId}`);
      await billingRef.set(dataToSave);

      // Update local state
      setBill({
        ...dataToSave,
        poId,
        billId,
      });

      toast.success("Bill approved successfully!");
    } catch (error) {
      console.error("Error approving bill:", error);
      toast.error("Error approving bill!");
    }
    setSpinner(false);
  };

  const handleCheck = async () => {
    try {
      setSpinner(true);

      // Mark as checked
      const dataToSave = {
        ...bill,
        status: "Checked",
        checkedBy: userContext.user.name || userContext.user.email,
        checkedAt: new Date().toLocaleString(),
        remarks,
      };

      // Remove unnecessary fields
      delete dataToSave.poId;
      delete dataToSave.billId;

      const billingRef = firebase
        .database()
        .ref(`billingInformation/${poId}/${billId}`);
      await billingRef.set(dataToSave);

      // Update local state
      setBill({
        ...dataToSave,
        poId,
        billId,
      });

      toast.success("Bill checked successfully!");
    } catch (error) {
      console.error("Error checking bill:", error);
      toast.error("Error checking bill!");
    }
    setSpinner(false);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleQuantityChange = (
    sectionId,
    itemId,
    newValue,
    isWorkOrderItem = true
  ) => {
    if (isReadOnly || !isEditMode) return;

    setEditedBill((prevState) => {
      const newState = { ...prevState };
      const section = isWorkOrderItem ? "workOrderItems" : "additionalItems";

      if (!newState[section]) {
        return prevState;
      }

      if (!newState[section][sectionId]) {
        return prevState;
      }

      if (!newState[section][sectionId].items[itemId]) {
        return prevState;
      }

      // Update item quantity
      newState[section][sectionId].items[itemId] = {
        ...newState[section][sectionId].items[itemId],
        totalQuantity: parseFloat(newValue),
        // Recalculate amount
        amount:
          parseFloat(newValue) *
          newState[section][sectionId].items[itemId].rate,
      };

      return newState;
    });
  };

  // Calculate totals
  const calculateTotal = (items = {}, section = "workOrderItems") => {
    let total = 0;

    if (!items) return total;

    Object.values(items).forEach((sectionItem) => {
      if (sectionItem.items) {
        Object.values(sectionItem.items).forEach((item) => {
          total += item.amount || 0;

          // Subtract deductions if any
          if (item.deductions) {
            Object.values(item.deductions).forEach((deduction) => {
              total -= deduction.amount || 0;
            });
          }
        });
      }
    });

    return total;
  };

  // Calculate all totals
  const workOrderTotal = calculateTotal(bill.workOrderItems, "workOrderItems");
  const additionalItemsTotal = calculateTotal(
    bill.additionalItems,
    "additionalItems"
  );
  const grandTotal = workOrderTotal + additionalItemsTotal;

  const canApprove =
    userContext.user.accountType === "Admin" ||
    userContext.user.accountType === "Owner";
  const canCheck = userContext.user.accountType === "Finance";
  const canEdit = !isReadOnly;

  return (
    <div className="bill-detail-container">
      {isSpinner ? (
        <div className="spinner-container">
          <CircularProgress style={{ color: "#fdd34d" }} />
        </div>
      ) : (
        <>
          <div className="bill-detail-header">
            <div
              className="back-button"
              onClick={() => history.push("/Billing")}
            >
              <FiArrowLeft size={20} />
              <span>Back to Billing</span>
            </div>

            <div className="bill-actions">
              {canEdit && !isEditMode && (
                <Button
                  content="Edit Bill"
                  onClick={() => setIsEditMode(true)}
                />
              )}

              {isEditMode && (
                <Button content="Save Changes" onClick={handleSave} />
              )}

              {canCheck && bill.status === "Raised" && !isEditMode && (
                <Button content="Mark as Checked" onClick={handleCheck} />
              )}

              {canApprove && bill.status === "Checked" && !isEditMode && (
                <Button content="Approve Bill" onClick={handleApprove} />
              )}

              <Button
                content={
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <FiPrinter size={16} /> Print
                  </div>
                }
                onClick={handlePrint}
              />
            </div>
          </div>

          <div className="bill-content">
            <div className="bill-paper">
              <div className="bill-header">
                <div className="bill-title">
                  <h2>{bill.billType || "Bill"}</h2>
                  <div
                    className={`status-indicator ${bill.status?.toLowerCase()}`}
                  >
                    {bill.status || "Draft"}
                  </div>
                </div>

                <div className="bill-info">
                  <div className="bill-info-item">
                    <span className="label">Bill No.:</span>
                    <span className="value">{bill.billNo}</span>
                  </div>
                  <div className="bill-info-item">
                    <span className="label">Date:</span>
                    <span className="value">{bill.date}</span>
                  </div>
                  <div className="bill-info-item">
                    <span className="label">PO ID:</span>
                    <span className="value">{bill.poId}</span>
                  </div>
                </div>
              </div>

              <div className="bill-details-grid">
                <div className="client-details">
                  <h3>Bill To</h3>
                  <div className="detail-block">
                    <p className="name">{bill.client?.name}</p>
                    <p className="address">{bill.client?.location}</p>
                  </div>
                </div>

                <div className="vendor-details">
                  <h3>Vendor</h3>
                  <div className="detail-block">
                    <p className="name">{bill.vendor?.name}</p>
                    <p className="company">{bill.vendor?.firm}</p>
                    <p className="category">{bill.vendor?.category}</p>
                  </div>
                </div>
              </div>

              <div className="bill-tabs">
                <div
                  className={`tab ${currentTab === 0 ? "active" : ""}`}
                  onClick={() => setCurrentTab(0)}
                >
                  Work Order Items
                </div>
                <div
                  className={`tab ${currentTab === 1 ? "active" : ""}`}
                  onClick={() => setCurrentTab(1)}
                >
                  Additional Items
                </div>
              </div>

              {currentTab === 0 && (
                <div className="bill-items-section">
                  <h3>Work Order Items</h3>

                  {bill.workOrderItems &&
                  Object.keys(bill.workOrderItems).length > 0 ? (
                    Object.entries(bill.workOrderItems).map(
                      ([sectionId, section]) => (
                        <div key={sectionId} className="bill-section">
                          <div className="section-header">
                            <h4>{section.section}</h4>
                            <div className="section-details">
                              <span>Category: {section.category}</span>
                              <span>Unit: {section.unit}</span>
                            </div>
                          </div>

                          <table className="items-table">
                            <thead>
                              <tr>
                                <th>S.No</th>
                                <th>Description</th>
                                <th>Dimensions</th>
                                <th>Nos</th>
                                <th className={isEditMode ? "editable" : ""}>
                                  Quantity
                                </th>
                                <th>Unit</th>
                                <th>Rate (₹)</th>
                                <th>Amount (₹)</th>
                              </tr>
                            </thead>
                            <tbody>
                              {section.items &&
                                Object.entries(section.items).map(
                                  ([itemId, item]) => (
                                    <tr key={itemId}>
                                      <td>{item.index}</td>
                                      <td>{item.description}</td>
                                      <td>
                                        {item.dimensions && (
                                          <>
                                            L: {item.dimensions.length}
                                            {item.dimensions.breadth > 0 &&
                                              ` x B: ${item.dimensions.breadth}`}
                                            {item.dimensions.height > 0 &&
                                              ` x H: ${item.dimensions.height}`}
                                          </>
                                        )}
                                      </td>
                                      <td>{item.nos}</td>
                                      <td
                                        className={isEditMode ? "editable" : ""}
                                      >
                                        {isEditMode ? (
                                          <input
                                            type="number"
                                            value={
                                              editedBill.workOrderItems?.[
                                                sectionId
                                              ]?.items?.[itemId]
                                                ?.totalQuantity ||
                                              item.totalQuantity
                                            }
                                            onChange={(e) =>
                                              handleQuantityChange(
                                                sectionId,
                                                itemId,
                                                e.target.value
                                              )
                                            }
                                            className="editable-input"
                                            step="0.01"
                                          />
                                        ) : (
                                          item.totalQuantity
                                        )}
                                      </td>
                                      <td>{item.units}</td>
                                      <td>₹ {item.rate}</td>
                                      <td>
                                        ₹{" "}
                                        {(isEditMode
                                          ? editedBill.workOrderItems?.[
                                              sectionId
                                            ]?.items?.[itemId]?.amount
                                          : item.amount
                                        )?.toFixed(2)}
                                      </td>
                                    </tr>
                                  )
                                )}

                              {/* Deductions for this section */}
                              {section.items &&
                                Object.entries(section.items).map(
                                  ([itemId, item]) =>
                                    item.deductions &&
                                    Object.entries(item.deductions).map(
                                      ([deductionId, deduction]) => (
                                        <tr
                                          key={deductionId}
                                          className="deduction-row"
                                        >
                                          <td></td>
                                          <td colSpan="2">
                                            <div className="deduction-description">
                                              <span>
                                                Deduction:{" "}
                                                {deduction.description}
                                              </span>
                                            </div>
                                          </td>
                                          <td>{deduction.nos}</td>
                                          <td>{deduction.totalQuantity}</td>
                                          <td>{deduction.units}</td>
                                          <td>₹ {deduction.rate}</td>
                                          <td className="deduction-amount">
                                            - ₹ {deduction.amount.toFixed(2)}
                                          </td>
                                        </tr>
                                      )
                                    )
                                )}
                            </tbody>
                            <tfoot>
                              <tr>
                                <td colSpan="7" className="total-label">
                                  Section Total
                                </td>
                                <td className="section-total">
                                  ₹{" "}
                                  {calculateTotal({
                                    [sectionId]: section,
                                  }).toFixed(2)}
                                </td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      )
                    )
                  ) : (
                    <div className="no-items">
                      No work order items in this bill
                    </div>
                  )}
                </div>
              )}

              {currentTab === 1 && (
                <div className="bill-items-section">
                  <h3>Additional Items</h3>

                  {bill.additionalItems &&
                  Object.keys(bill.additionalItems).length > 0 ? (
                    Object.entries(bill.additionalItems).map(
                      ([sectionId, section]) => (
                        <div key={sectionId} className="bill-section">
                          <div className="section-header">
                            <h4>{section.section}</h4>
                            <div className="section-details">
                              <span>Category: {section.category}</span>
                              <span>Unit: {section.unit}</span>
                            </div>
                          </div>

                          <table className="items-table">
                            <thead>
                              <tr>
                                <th>S.No</th>
                                <th>Description</th>
                                <th>Dimensions</th>
                                <th>Nos</th>
                                <th className={isEditMode ? "editable" : ""}>
                                  Quantity
                                </th>
                                <th>Unit</th>
                                <th>Rate (₹)</th>
                                <th>Amount (₹)</th>
                              </tr>
                            </thead>
                            <tbody>
                              {section.items &&
                                Object.entries(section.items).map(
                                  ([itemId, item]) => (
                                    <tr key={itemId}>
                                      <td>{item.index}</td>
                                      <td>{item.description}</td>
                                      <td>
                                        {item.dimensions && (
                                          <>
                                            L: {item.dimensions.length}
                                            {item.dimensions.breadth > 0 &&
                                              ` x B: ${item.dimensions.breadth}`}
                                            {item.dimensions.height > 0 &&
                                              ` x H: ${item.dimensions.height}`}
                                          </>
                                        )}
                                      </td>
                                      <td>{item.nos}</td>
                                      <td
                                        className={isEditMode ? "editable" : ""}
                                      >
                                        {isEditMode ? (
                                          <input
                                            type="number"
                                            value={
                                              editedBill.additionalItems?.[
                                                sectionId
                                              ]?.items?.[itemId]
                                                ?.totalQuantity ||
                                              item.totalQuantity
                                            }
                                            onChange={(e) =>
                                              handleQuantityChange(
                                                sectionId,
                                                itemId,
                                                e.target.value,
                                                false
                                              )
                                            }
                                            className="editable-input"
                                            step="0.01"
                                          />
                                        ) : (
                                          item.totalQuantity
                                        )}
                                      </td>
                                      <td>{item.units}</td>
                                      <td>₹ {item.rate}</td>
                                      <td>
                                        ₹{" "}
                                        {(isEditMode
                                          ? editedBill.additionalItems?.[
                                              sectionId
                                            ]?.items?.[itemId]?.amount
                                          : item.amount
                                        )?.toFixed(2)}
                                      </td>
                                    </tr>
                                  )
                                )}

                              {/* Deductions for this section */}
                              {section.items &&
                                Object.entries(section.items).map(
                                  ([itemId, item]) =>
                                    item.deductions &&
                                    Object.entries(item.deductions).map(
                                      ([deductionId, deduction]) => (
                                        <tr
                                          key={deductionId}
                                          className="deduction-row"
                                        >
                                          <td></td>
                                          <td colSpan="2">
                                            <div className="deduction-description">
                                              <span>
                                                Deduction:{" "}
                                                {deduction.description}
                                              </span>
                                            </div>
                                          </td>
                                          <td>{deduction.nos}</td>
                                          <td>{deduction.totalQuantity}</td>
                                          <td>{deduction.units}</td>
                                          <td>₹ {deduction.rate}</td>
                                          <td className="deduction-amount">
                                            - ₹ {deduction.amount.toFixed(2)}
                                          </td>
                                        </tr>
                                      )
                                    )
                                )}
                            </tbody>
                            <tfoot>
                              <tr>
                                <td colSpan="7" className="total-label">
                                  Section Total
                                </td>
                                <td className="section-total">
                                  ₹{" "}
                                  {calculateTotal({
                                    [sectionId]: section,
                                  }).toFixed(2)}
                                </td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      )
                    )
                  ) : (
                    <div className="no-items">
                      No additional items in this bill
                    </div>
                  )}
                </div>
              )}

              <div className="bill-summary">
                <div className="bill-total-section">
                  <div className="total-row">
                    <span>Work Order Items Total:</span>
                    <span>₹ {workOrderTotal.toFixed(2)}</span>
                  </div>
                  <div className="total-row">
                    <span>Additional Items Total:</span>
                    <span>₹ {additionalItemsTotal.toFixed(2)}</span>
                  </div>
                  <div className="total-row grand-total">
                    <span>Grand Total:</span>
                    <span>₹ {grandTotal.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              <div className="bill-remarks">
                <h3>Remarks</h3>
                {isEditMode ? (
                  <textarea
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    placeholder="Add remarks for this bill..."
                    rows={4}
                  />
                ) : (
                  <p>{bill.remarks || "No remarks"}</p>
                )}
              </div>

              <div className="bill-signatures">
                <div className="signature-block">
                  <p className="signature-title">Raised by</p>
                  <p>{bill.raisedBy}</p>
                  <p className="signature-date">{bill.raisedAt}</p>
                </div>

                {bill.checkedBy && (
                  <div className="signature-block">
                    <p className="signature-title">Checked by</p>
                    <p>{bill.checkedBy}</p>
                    <p className="signature-date">{bill.checkedAt}</p>
                  </div>
                )}

                {bill.approvedBy && (
                  <div className="signature-block">
                    <p className="signature-title">Approved by</p>
                    <p>{bill.approvedBy}</p>
                    <p className="signature-date">{bill.approvedAt}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default drawerTemplate1(BillDetail);
